import { Controller } from "@hotwired/stimulus";

/**
 * <div data-controller="event-ids">
 *   <turbo-frame id="event-ids" data-event-ids-target="hiddenContainer">
 *     <input type="hidden" value="1">
 *   </turbo-frame>
 *   <form>
 *     <input type="checkbox" data-event-ids-target="checkDisable" value="1">
 *   </form>
 *   <div data-event-id-target="remove" data-event-id="1">
 *     event 1
 *     <button data-action="event-ids#remove" data-event-ids-id-param="1">remove</button>
 *   </div>
 * </div>
 */
export class EventIdsController extends Controller {
  static values = {
    /** 内部で使用 */
    ids: Array,
  };
  static targets = [
    /** <input type=hidden name=event_ids[]> の親 (入力|出力) */
    "hiddenContainer",
    /** <input type=hidden name=event_ids[]> の親 (出力) */
    "outputHiddenContainer",
    /** チェックボックス */
    "input",
    /** idが存在する時チェックする */
    "check",
    /** idが存在する時disabledにする */
    "disable",
    /** idが存在しない時取り除く */
    "remove",
    /** idが存在する時表示する */
    "show",
  ];

  connect() {
    console.debug("connect EventIdsController");
    console.debug("hiddenContainerTargets", this.hiddenContainerTargets);
    console.debug("checkTargets", this.checkTargets);
    console.debug("disableTargets", this.disableTargets);
    console.debug("showTargets", this.showTargets);
  }

  // hiddenContainerTargetsが追加された時にDOMから状態を取得する
  // Turbo FrameやTurbo Streamで追加されることを想定
  hiddenContainerTargetConnected(container) {
    console.debug("hiddenContainerTargetConnedted", container.innerHTML);
    this.#update([...container.querySelectorAll("[type=hidden][value]")].map((v) => v.value));
  }

  // 各targetが追加された時に状態をDOMへ反映する

  checkTargetConnected(input) {
    console.debug("checkTargetConnected", input);
    this.#renderChecks([input]);
  }

  disableTargetConnected(input) {
    console.debug("disableTargetConnected", input);
    this.#renderDisables([input]);
  }

  outputHiddenContainerTargetConnected(container) {
    console.debug("outputHiddenContainerTargetConnected", container.innerHTML);
    this.#renderOutputHiddenContainers();
  }

  showTargetConnected(el) {
    console.debug("showTargetConnected", el);
    this.#renderShows([el]);
  }

  remove({ params: { id } }) {
    console.debug("remove", id);
    const s = String(id);
    this.#update(this.idsValue.filter((v) => v !== s));
  }

  /** inputTargetsの値を追加 */
  addInputs() {
    const ids = this.inputTargets
      .filter((input) => {
        if (input.disabled) return false;
        if (input.type === "checkbox" || input.type === "radio") return input.checked;
      })
      .map((input) => input.value);
    console.debug("addInputs", ids, this.idsValue);
    this.#update([...this.idsValue, ...ids]);
  }

  #previouseIds = [];
  #update(ids) {
    console.debug("#update", ids);
    this.idsValue = ids.map(String);
    // renderが重めなので変更があった時だけ実行
    if (this.#previouseIds.join() === this.idsValue.join()) return;
    this.#previouseIds = this.idsValue;
    this.#render();
  }

  #render() {
    this.#renderHiddenContainers();
    this.#renderOutputHiddenContainers();
    this.#renderChecks();
    this.#renderDisables();
    this.#renderRemoves();
    this.#renderShows();
  }

  // hiddenContainerTargetsの中身をidsに合わせて更新
  #renderHiddenContainers() {
    for (const container of this.hiddenContainerTargets) {
      this.#replaceHiddens(container);
    }
  }

  // outputHiddenContainerTargetsの中身をidsに合わせて更新
  #renderOutputHiddenContainers() {
    for (const container of this.outputHiddenContainerTargets) {
      this.#replaceHiddens(container);
    }
  }

  #replaceHiddens(container) {
    // 消して全て追加
    container.innerHTML = "";
    for (const id of this.idsValue) {
      const hidden = document.createElement("input");
      hidden.type = "hidden";
      hidden.name = container.dataset.name || "event_ids[]";
      hidden.value = id;
      container.appendChild(hidden);
    }
  }

  // checkTargetsの内idsに存在する物をチェック
  #renderChecks(targets = this.checkTargets) {
    for (const input of targets) {
      input.checked = this.idsValue.includes(input.value);
    }
  }

  // disableTargetsの内idsに存在する物をdisable
  #renderDisables(targets = this.disableTargets) {
    for (const input of targets) {
      console.debug("disable", input.value, this.idsValue);
      input.disabled = this.idsValue.includes(input.value);
    }
  }

  // removeTargetsの内idsに存在しない物を削除
  #renderRemoves() {
    for (const el of this.removeTargets) {
      if (!this.idsValue.includes(el.dataset.eventId)) {
        el.remove();
      }
    }
  }

  // idsに存在しない物を非表示
  #renderShows(targets = this.showTargets) {
    for (const el of targets) {
      console.debug("show", [el.dataset.eventId, this.idsValue]);
      if (this.idsValue.includes(el.dataset.eventId)) {
        el.style.display = el.dataset.defaultDisplay || "block";
      } else {
        el.dataset.defaultDisplay ??= getComputedStyle(el).display;
        el.style.display = "none";
      }
    }
  }
}
